import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { Pressable } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanModality } from '../data/npTestPlanTypes'

export interface NpTestPlanModalityToggleProps {
  value: NpTestPlanModality
  onChange: (modality: NpTestPlanModality) => void
  disabled?: boolean
}

const Container = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  flexDirection: 'row',
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  borderRadius: '24px',
}))

const Segment = styled(Pressable)<{ theme: ThemeType; isSelected: boolean }>(
  ({ theme: { colors, spacing }, isSelected }) => ({
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing['12px'],
    paddingHorizontal: spacing['24px'],
    borderRadius: '24px',
    ...(isSelected && {
      backgroundColor: colors.backgroundHighlightPrimary,
      borderWidth: 1,
      borderColor: colors.borderActive,
      borderStyle: 'solid',
      margin: '-1px',
    }),
  }),
)

/**
 * A radiogroup rather than a segmented button so screen readers announce the selection rather than
 * two independent buttons.
 */
export const NpTestPlanModalityToggle: FunctionComponent<NpTestPlanModalityToggleProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType

  const options = [
    {
      modality: NpTestPlanModality.VIRTUAL,
      displayName: formatMessage({
        defaultMessage: 'Virtual',
        description: 'Option to build a neuropsych testing battery delivered over video',
      }),
    },
    {
      modality: NpTestPlanModality.IN_PERSON,
      displayName: formatMessage({
        defaultMessage: 'In person',
        description: 'Option to build a neuropsych testing battery delivered in person',
      }),
    },
  ]

  return (
    <Container testID={tID('NpTestPlanModalityToggle')} accessibilityRole='radiogroup'>
      {options.map(({ modality, displayName }) => {
        const isSelected = modality === value
        return (
          <Segment
            key={modality}
            testID={tID(`NpTestPlanModalityToggle-${modality}`)}
            accessibilityRole='radio'
            accessibilityState={{ checked: isSelected, disabled: Boolean(disabled) }}
            aria-checked={isSelected}
            isSelected={isSelected}
            disabled={disabled}
            onPress={() => onChange(modality)}
          >
            <BodyText
              text={displayName}
              size={BodyTextSize.DEFAULT}
              color={isSelected ? colors.textActive : colors.textPrimary}
            />
          </Segment>
        )
      })}
    </Container>
  )
}
